import React from "react";
import { useSelector } from "react-redux";
import { Drawer, Row, Col, Divider } from "antd";
import Button from "@dtci/spellbook/Button";
import Select from "@dtci/spellbook/Select";
import { DetailRow } from "tap/components/DetailRow";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import {
  getWindowStartDateLabel,
  getPricingTierLabel,
  getStatusConfig,
} from "../../../utils/windowHelpers";
import "./styles.scss";

function WindowSequenceDetails({ data }) {
  if (!data) return null;

  const statusConfig = getStatusConfig(data.status);

  return (
    <div className="window-sequence-details">
      <div className="window-sequence-details-title-block">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div className="window-sequence-details-id">
            Window Sequence #{data.id}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 16,
              backgroundColor: statusConfig.color + "20",
              border: `1px solid ${statusConfig.color}40`,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: statusConfig.color,
                textTransform: "uppercase",
              }}
            >
              {data.status}
            </span>
          </div>
        </div>
        <h2 className="window-sequence-details-name">
          {data.windowSequenceName || "Untitled Window Sequence"}
        </h2>
      </div>

      <div className="windows-section">
        <div className="section-header">
          <span className="section-header-title">Windows</span>
          {data?.windows?.length > 0 && (
            <span className="section-header-count">
              {data.windows.length} items
            </span>
          )}
        </div>
        {data?.windows?.length > 0 ? (
          data.windows.map((win, idx) => (
            <div key={idx} className="window-item">
              <div className="window-item-header">
                <span className="window-item-title">
                  {idx + 1}. {win.windowName}
                </span>
              </div>
              <div className="window-item-details">
                {idx > 0 && (
                  <DetailRow
                    label="Previous Window +1 Start"
                    value={win.previousWindowPlusOneStart ? "Yes" : "No"}
                  />
                )}
                <DetailRow
                  label="Window Start Date"
                  value={getWindowStartDateLabel(win.windowStartDate)}
                />
                <DetailRow
                  label="Duration"
                  value={
                    win.windowDuration
                      ? `${win.windowDuration} ${win.windowDurationUnit || ""}`
                      : "—"
                  }
                />
                <DetailRow
                  label="Use Pricing Tier From"
                  value={getPricingTierLabel(win.usePricingTierFrom)}
                  noBorder
                />
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">No windows defined</div>
        )}
      </div>
    </div>
  );
}

export const ViewWindowSequenceDrawer = () => {
  const {
    isViewDrawerOpen,
    selectedWindowSequence,
    windowSequenceHistory,
    selectedHistory,
  } = useSelector((state) => state.windows);
  const { updateWindows, clearWindowForm } = useWindowsActions();

  const handleClose = () => {
    updateWindows({
      isViewDrawerOpen: false,
    });
  };

  // Form reset deferred to afterOpenChange to allow close animation to complete
  const handleAfterOpenChange = (open) => {
    if (!open) {
      clearWindowForm();
    }
  };

  const drawerHeader = (
    <div className="view-window-sequence-drawer-header">
      <span className="view-window-sequence-drawer-title">Window Sequence</span>
      <div className="view-window-sequence-drawer-actions">
        <Select
          placeholder="History"
          options={windowSequenceHistory}
          isClearable
          isSearchable
          onChange={(value) => updateWindows({ selectedHistory: value })}
          value={selectedHistory}
        />
      </div>
    </div>
  );

  const drawerWidth = selectedHistory ? 1000 : 750;

  return (
    <Drawer
      className="view-drawer windows-view-drawer"
      title={drawerHeader}
      width={drawerWidth}
      placement="right"
      open={isViewDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      destroyOnClose
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div
          className="button-row"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      }
    >
      <Row gutter={32} align="top" style={{ display: "flex" }}>
        <Col
          span={selectedHistory ? 11 : 24}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <WindowSequenceDetails data={selectedWindowSequence} />
        </Col>
        {selectedHistory && (
          <>
            <Col span={2} style={{ display: "flex", justifyContent: "center" }}>
              <Divider type="vertical" style={{ height: "100%", margin: 0 }} />
            </Col>
            <Col span={11} style={{ display: "flex", flexDirection: "column" }}>
              <WindowSequenceDetails data={selectedHistory.data} />
            </Col>
          </>
        )}
      </Row>
    </Drawer>
  );
};
